import { useQuery, useQueryClient, UseQueryOptions } from "react-query"
import { useApiClient } from "../../api-client/api-client.hooks"
import { TApiErrors, TApiProjectSection } from "../../api-client/types"
import { TApiTasksFilters } from "../../api-client/tasks-api"
import { useProjectQuery } from "../../projects/projects.hooks"

interface Params {
  projectId: string
  filters?: TApiTasksFilters
}

interface Result {
  projectSections: TApiProjectSection[]
}

export const useProjectSectionsQuery = (params: Params, options?: UseQueryOptions<Result, TApiErrors>) => {
  const { projectId, filters } = params

  const apiClient = useApiClient()
  const queryClient = useQueryClient()

  // Get project from cache on start, assumption is that project sections will be fetched only
  // in places where given project is already fetched?
  const projectQuery = useProjectQuery({
    projectId: projectId
  })

  const project = projectQuery.data?.project || undefined

  const query = useQuery<Result, TApiErrors>(
    ["project-sections", projectId, filters],
    async () => {

      const response = await apiClient.getProjectSections(
        projectId,
        filters,
      )

      if (!response.ok) {
        throw response.errors
      }

      // Extend tasks with projects from cache!
      const projectSections = response.projectSections.map((ps) => {
        return {
          ...ps,
          tasks: {
            ...ps.tasks,
            tasks: ps.tasks.tasks.map((task) => {
              return {
                ...task,
                project: project,
              }
            })
          }
        }
      }) as TApiProjectSection[]

      return { 
        projectSections: projectSections
      }
    },
    {
      ...options,
      // This ensures the fetching of project sections will not happen before projects are not fetched before
      enabled: !!project && (options.enabled !== undefined ? options.enabled : true),
      onSuccess: (data) => {
        if (options.onSuccess) options.onSuccess(data)

        // Preload task details for each task for cache if not already cached
        for (const projectSection of data.projectSections) {
          for (const task of projectSection.tasks.tasks) {
            const queryKey = ['project-task', projectSection.projectId, task.id]
            const existing = queryClient.getQueryData(queryKey)

            if (!existing) {
              queryClient.setQueryData(queryKey, {
                task: task
              })
            }
          }
        }
      }
    }
    /* // this should be part of the feature, not the service?
      {
        refetchOnWindowFocus: false,
      }
    */
  )

  return {
    projectSectionsQuery: query,
  }
}